/**
 * Format financial values in European format: € 11.000,99
 * @param {number} value - The number to format
 * @param {boolean} showCurrency - Whether to show the € symbol (default: true)
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, showCurrency = true) => {
  if (value === null || value === undefined || isNaN(value)) {
    return showCurrency ? '€ 0,00' : '0,00';
  }
  
  // Format with 2 decimals, European style (dot for thousands, comma for decimals)
  const formatted = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
  
  return showCurrency ? `€ ${formatted}` : formatted;
};

/**
 * Format non-financial numbers in European format: 11.000 (no decimals)
 * @param {number} value - The number to format
 * @returns {string} Formatted number string
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  
  // Format with no decimals, European style (dot for thousands)
  return new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

/**
 * Parse European formatted currency string to number
 * Converts "€ 11.000,99" or "11.000,99" to 11000.99
 * @param {string} str - The string to parse
 * @returns {number} Parsed number
 */
export const parseCurrency = (str) => {
  if (!str || typeof str !== 'string') return 0;
  
  // Remove currency symbol and spaces
  let cleaned = str.replace(/€/g, '').trim();
  
  // Replace dots (thousands separator) with nothing
  cleaned = cleaned.replace(/\./g, '');
  
  // Replace comma (decimal separator) with dot
  cleaned = cleaned.replace(/,/g, '.');
  
  return parseFloat(cleaned) || 0;
};

/**
 * Parse European formatted number string to number
 * Converts "11.000" to 11000
 * @param {string} str - The string to parse
 * @returns {number} Parsed number
 */
export const parseNumber = (str) => {
  if (!str || typeof str !== 'string') return 0;
  
  // Remove dots (thousands separator)
  const cleaned = str.replace(/\./g, '');
  
  return parseInt(cleaned, 10) || 0;
};

/**
 * Format datetime as LOCAL time without timezone conversion
 * CRITICAL: Times are already stored in local Brussels time - NO CONVERSION NEEDED
 * @param {string} isoString - ISO datetime string (e.g., "2024-12-20T15:30:00")
 * @param {boolean} includeDate - Whether to include date (default: true)
 * @returns {string} Formatted datetime string (e.g., "20/12/2024 15:30" or "15:30")
 */
export const formatLocalDateTime = (isoString, includeDate = true) => {
  if (!isoString || isoString === 'N/A') return 'N/A';
  
  try {
    // Remove timezone info if present (strip +XX:XX, -XX:XX, Z suffix)
    let cleanStr = isoString.split('.')[0]; // Remove milliseconds
    cleanStr = cleanStr.split('+')[0].split('-').slice(0, 3).join('-'); // Remove positive timezone
    cleanStr = cleanStr.split('Z')[0]; // Remove Z suffix
    
    // Parse the date parts manually to avoid any timezone conversion
    const parts = cleanStr.split('T');
    if (parts.length !== 2) return isoString;
    
    const datePart = parts[0]; // YYYY-MM-DD
    const timePart = parts[1].substring(0, 5); // HH:MM
    
    if (includeDate) {
      // Convert YYYY-MM-DD to DD/MM/YYYY
      const dateComponents = datePart.split('-');
      if (dateComponents.length === 3) {
        const [year, month, day] = dateComponents;
        return `${day}/${month}/${year} ${timePart}`;
      }
    }
    
    return timePart; // Just return time if date not needed
  } catch (e) {
    console.error('Error formatting local datetime:', e);
    return isoString;
  }
};

/**
 * Format time only from datetime string as LOCAL time without timezone conversion
 * CRITICAL: Times are already stored in local Brussels time - NO CONVERSION NEEDED
 * @param {string} isoString - ISO datetime string
 * @returns {string} Formatted time string (e.g., "15:30")
 */
export const formatLocalTime = (isoString) => {
  return formatLocalDateTime(isoString, false);
};

/**
 * Format date from datetime string
 * @param {string} dateString - Date string (YYYY-MM-DD or ISO datetime)
 * @returns {string} Formatted date string (e.g., "20/12/2024")
 */
export const formatLocalDate = (dateString) => {
  if (!dateString || dateString === 'N/A') return 'N/A';
  
  try {
    // Extract just the date part if it's a datetime
    const datePart = dateString.split('T')[0];
    const dateComponents = datePart.split('-');
    
    if (dateComponents.length === 3) {
      const [year, month, day] = dateComponents;
      return `${day}/${month}/${year}`;
    }
    
    return dateString;
  } catch (e) {
    console.error('Error formatting local date:', e);
    return dateString;
  }
};
